import { useCallback, useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import moment from 'moment';

import sharedMessages from 'Components/utils/sharedMessages';
import EventHandler from 'Screens/Dashboard/Events/EventHandler/EventHandler';
import { defaultValues, EventProvider, useEventContext } from 'Screens/Dashboard/Events/EventHandler/EntityEventContext';
import { useCreateEventMutation } from 'api/eventEndpoints';
import useGetCurrentEntityId from 'hooks/useGetCurrentEntityId';
import { toDateTimeFormat } from 'utils/timeDateUtils';
import useCurrentEntityUsesPoints from 'hooks/useCurrentEntityUsesPoints';
import { normalizeHowLongForSubmit } from 'Screens/Dashboard/Events/EventHandler/inputs/EventRecurrence/recurrenceUtils';
import useGetUser from 'hooks/useGetUser';

type Props = {
    open?: boolean;
    onClose?: () => void;
}

const AddEventHandlerContainer = (props: Props) => (
    <EventProvider>
        <AddEventHandler {...props} />
    </EventProvider>
);

const AddEventHandler = ({
    open: controlledOpen,
    onClose,
}: Props) => {
    const intl = useIntl();
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
    const { values, updateValues, resetValues } = useEventContext();
    const currentEntityUsesPoints = useCurrentEntityUsesPoints();
    const user = useGetUser();

    const currentEntityId = useGetCurrentEntityId();
    const [addEvent, { isLoading }] = useCreateEventMutation();

    const open = controlledOpen ?? uncontrolledOpen;
    const selectedEntityId = values.entityId ?? currentEntityId;
    const selectedEntity = useMemo(
        () => user?.entities.find((entity) => entity.id === selectedEntityId),
        [selectedEntityId, user?.entities],
    );
    const usesPoints = selectedEntity?.usesPoints ?? currentEntityUsesPoints;

    useEffect(() => {
        if (open && currentEntityId && !values.entityId) {
            updateValues({ entityId: currentEntityId });
        }
    }, [currentEntityId, open, updateValues, values.entityId]);

    const resetForm = () => {
        resetValues({
            ...defaultValues,
            recurrence: { ...defaultValues.recurrence },
            howLong: { mode: 0 },
            attendees: [],
            categoryIds: [],
            kind: 'Local',
            scope: 'LocalOnly',
            targetEntityIds: [],
            color: '#3174AD',
            priests: [],
            priestIds: [],
            absencePenaltyPoints: null,
            qrConfirmationWindowMinutes: 60,
            attendeeReminderMinutesBefore: null,
            entityId: currentEntityId ?? undefined,
        });
    };

    const onCloseRequest = () => {
        resetForm();

        if (onClose) {
            onClose();
            return;
        }

        setUncontrolledOpen(false);
    };

    const timeToDate = useCallback((value: string, date: string) => {
        const currentDate = moment(date);
        if (!value) {
            return toDateTimeFormat(currentDate.startOf('day'));
        }

        const time = moment(value, 'HH:mm');
        const updatedDate = currentDate.set({
            hour: time.hour(),
            minute: time.minute(),
            second: 0,
            millisecond: 0,
        });

        return toDateTimeFormat(updatedDate);
    }, []);

    const handleSubmit = async () => {
        const submitEntityId = values.entityId ?? currentEntityId;

        if (!submitEntityId) {
            return;
        }

        try {
            const dateTo = values.isMultiDay ? values.dateTo : values.dateFrom;
            const eventKind = values.kind ?? 'Local';

            const howLong = normalizeHowLongForSubmit(values.recurrence, values.howLong);

            await addEvent({
                entityId: submitEntityId,
                data: {
                    title: values.title,
                    description: values.description,
                    eventPlace: values.eventPlace,
                    dateFrom: timeToDate(values.timeFrom, values.dateFrom),
                    dateTo: timeToDate(values.timeTo, dateTo),
                    allDay: values.allDay,
                    visibleForGuests: values.visibleForGuests,
                    email: '',
                    pointsPerEvent: usesPoints ? values.pointsPerEvent : 0,
                    optionalPointsPerEvent: usesPoints ? values.optionalPointsPerEvent : null,
                    absencePenaltyPoints: usesPoints ? values.absencePenaltyPoints ?? null : null,
                    qrConfirmationWindowMinutes: usesPoints ? values.qrConfirmationWindowMinutes : 60,
                    attendeeReminderMinutesBefore: values.attendeeReminderMinutesBefore ?? null,
                    recurrence: values.recurrence,
                    ...(howLong ? { howLong } : {}),
                    categoryIds: values.categoryIds,
                    attendees: eventKind === 'Global' ? [] : values.attendees,
                    kind: eventKind,
                    scope: eventKind === 'Global' ? values.scope ?? 'GlobalAllChildren' : 'LocalOnly',
                    targetEntityIds: eventKind === 'Global' && values.scope === 'GlobalSelectedChildren'
                        ? values.targetEntityIds ?? []
                        : [],
                    color: values.color ?? '#3174AD',
                    priests: values.priests ?? [],
                    priestIds: values.priestIds ?? [],
                },
            }).unwrap();
            onCloseRequest();
        } catch {
            // TO_DO: show submit error state
        }
    };

    return (
        <EventHandler
            open={open}
            isLoading={isLoading}
            modalHeading={intl.formatMessage(messages.addEvent)}
            primaryButtonText={intl.formatMessage(sharedMessages.add)}
            handleSubmit={handleSubmit}
            onCloseRequest={onCloseRequest}
        />
    );
};

const messages = defineMessages({
    addEvent: {
        id: 'src.Screens.Dashboard.Events.EventHandler.EventHandler.addEvent',
        defaultMessage: 'Dodaj wydarzenie',
    },
    modalTitle: {
        id: 'src.Screens.Dashboard.Events.EventHandler.EventHandler.title',
        defaultMessage: 'Czy na pewno chcesz zakończyć dodawanie wydarzenia?',
    },
});

export default AddEventHandlerContainer;
